import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext, useDocument } from "@automerge/react";
import { RedirectToSignIn, SignedIn, SignedOut } from "@clerk/clerk-react";

import "@/components/mosaic.css";
import { useMutation } from "@tanstack/react-query";
import {
  createFileRoute,
  getRouteApi,
  redirect,
  useRouter,
} from "@tanstack/react-router";
import { useTreeExpansion } from "forest/file-tree/use-expansion";
import { useFileCommands } from "forest/file-tree/use-file-commands";
import { useHiddenFiles } from "forest/file-tree/use-hidden-files";
import { useMaterializedFiles } from "forest/file-tree/use-materialized-files";
import type { EditorSelection } from "forest/monaco-editor/MonacoEditor";
import { History, KeyboardIcon, SaveIcon, XIcon } from "lucide-react";
import { captureAndCreateCheckpoint } from "playground/checkpoints";
import { resolveInode, resolveParent } from "playground/fs/paths";
import { migrateProjectFiles } from "playground/project";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  Mosaic,
  MosaicWindow,
  MosaicWindowContext,
  getLeaves,
  type MosaicNode,
  type MosaicPath,
} from "react-mosaic-component";
import { toast } from "sonner";
import { invariant } from "ts-invariant";

import { client } from "@/client";
import BearSandboxProvider, {
  useBearSandbox,
} from "@/components/BearSandboxProvider";
import Header from "@/components/Header";
import PaneSuspense from "@/components/PaneSuspense";
import {
  ProjectCommandMenu,
  type CommandMenuGroup,
} from "@/components/ProjectCommandMenu";
import ProjectFileIcon from "@/components/ProjectFileIcon";
import { PROJECT_FILE_PROXY_PATH } from "@/components/ProjectFileLink";
import ProjectFileList from "@/components/ProjectFileList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CommandDialog } from "@/components/ui/command";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { useIsMac } from "@/hooks/use-is-mac";
import { isAutomergeUrl } from "@/lib/automerge-repo";
import { useEditorSettings } from "@/lib/editor-settings";
import {
  FloorplanEditorProvider,
  useFloorplanEditor,
} from "@/lib/floorplan-editor-context";
import { ProjectSourceProvider } from "@/lib/project-source";
import BuilderPane from "@/panes/BuilderPane";
import CommandCenterPane from "@/panes/CommandCenterPane";
import DocPane from "@/panes/DocPane";
import HistoryPane from "@/panes/HistoryPane";
import { projectTools } from "@/panes/project-tool-registry";
import type { ProjectTool } from "@/panes/project-tools";
import SketchPane from "@/panes/SketchPane";
import SourcePane from "@/panes/SourcePane";

import "react-mosaic-component/react-mosaic-component.css";
import { WorkspaceProvider, useWorkspace } from "@/panes/Workspace";
import { FullScreenLoader } from "@/routes/__root";
import type { Project } from "@/types/projects";

type FileKind = "doc" | "code" | "sketch" | "history" | "plaintext";

type FileTab = {
  id: string;
  filename: string;
  kind: FileKind;
  title: string;
};

type PaneId = string;

const COMMAND_CENTER_PANE_ID = "command-center";
const FILE_TAB_PREFIX = "file:";
const HISTORY_TAB_PREFIX = "history:";
const TOOL_BY_ID = new Map<string, ProjectTool>(
  projectTools.map((tool) => [tool.id, tool]),
);
const route = getRouteApi("/p/$projectId/");

function getFileKind(filename: string): FileKind | null {
  const extension = filename.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "md":
    case "mdx":
      return "doc";
    case "ts":
    case "tsx":
      return "code";
    case "tldr":
      return "sketch";
    default:
      return null;
  }
}

function resolveProjectFileHref(reference?: string): string | undefined {
  if (!reference) {
    return undefined;
  }

  if (reference.startsWith("automerge:")) {
    return undefined;
  }

  if (reference.startsWith(PROJECT_FILE_PROXY_PATH)) {
    return reference;
  }

  if (reference.startsWith("s3://")) {
    return `${PROJECT_FILE_PROXY_PATH}?source=${encodeURIComponent(reference)}`;
  }

  return reference;
}

function getFileTabId(filename: string): string {
  return `${FILE_TAB_PREFIX}${filename}`;
}

function getHistoryTabId(filename: string): string {
  return `${HISTORY_TAB_PREFIX}${filename}`;
}

function createFileTab(
  filename: string,
  kind: FileKind,
  existingTabs: FileTab[],
): FileTab {
  let title = filename;

  if (kind === "sketch") {
    const baseName = filename.replace(/\.tldr$/i, "");
    if (baseName.length > 0) {
      title = baseName;
    } else {
      const sketchCount = existingTabs.filter(
        (tab) => tab.kind === "sketch",
      ).length;
      title = `Sketch ${sketchCount + 1}`;
    }
  }

  if (kind === "history") {
    title = `${filename} History`;
  }

  const id =
    kind === "history" ? getHistoryTabId(filename) : getFileTabId(filename);

  return {
    id,
    filename,
    kind,
    title,
  };
}

export const Route = createFileRoute("/p/$projectId/")({
  pendingMs: 0,
  pendingComponent: FullScreenLoader,
  loader: async ({ params }) => {
    const res = await fetch(`/api/projects/${params.projectId}`, {
      credentials: "include",
    });
    if (res.status === 401) {
      throw redirect({ to: "/" });
    }
    if (!res.ok) throw new Error("Project not found");
    return (await res.json()) as { indexUrl: string };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { projectId } = Route.useParams();
  const { indexUrl } = Route.useLoaderData();
  const [project, changeProject] = useDocument<Project>(
    indexUrl as AutomergeUrl,
    {
      suspense: true,
    },
  );

  // Summarize fires after `playground.upload()` succeeds for PDFs/images.
  // Lives at the route level (not inside ProjectWorkspace) so it can be
  // wired into BearSandboxProvider's `onAfterUpload` HAL hook.
  const summarizeFile = useMutation({
    mutationFn: async ({
      source,
      mimeType,
    }: {
      source: string;
      mimeType: string;
      filename: string;
    }) => {
      const res = await client.api.files.summarize.$post({
        json: { source, mimeType },
      });
      return (await res.json()) as { description: string };
    },
    onSuccess: ({ description }, { filename }) => {
      changeProject((doc) => {
        const resolved = resolveParent(doc.files, filename);
        if (resolved) {
          const inode = resolved.parentEntries[resolved.basename];
          if (inode) inode.description = description;
        }
      });
    },
    onError: (err) => console.error("Failed to summarize file", err),
  });

  const handleAfterUpload = useCallback(
    (payload: { filename: string; fileUrl: string; mimeType: string }) => {
      if (
        payload.mimeType === "application/pdf" ||
        payload.mimeType.startsWith("image/")
      ) {
        summarizeFile.mutate({
          source: payload.fileUrl,
          mimeType: payload.mimeType,
          filename: payload.filename,
        });
      }
    },
    [summarizeFile],
  );

  return (
    <>
      <SignedIn>
        <WorkspaceProvider className="flex h-screen w-screen flex-col">
          <FloorplanEditorProvider projectFiles={project.files}>
            <ProjectSourceProvider project={project}>
              <BearSandboxProvider
                project={project}
                projectId={projectId}
                indexUrl={indexUrl as AutomergeUrl}
                onAfterUpload={handleAfterUpload}
              >
                <ProjectWorkspace
                  project={project}
                  changeProject={changeProject}
                  indexUrl={indexUrl as AutomergeUrl}
                />
              </BearSandboxProvider>
            </ProjectSourceProvider>
          </FloorplanEditorProvider>
        </WorkspaceProvider>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

interface ProjectWorkspaceProps {
  project: Project;
  changeProject(update: (doc: Project) => void): void;
  indexUrl: AutomergeUrl;
}

function ProjectWorkspace({
  project,
  changeProject,
  indexUrl,
}: ProjectWorkspaceProps) {
  const { projectId } = route.useParams();
  const router = useRouter();
  const { vimMode, setVimMode } = useEditorSettings();
  const isMac = useIsMac();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Set page title to include project name
  useEffect(() => {
    document.title = `${project.name} — Bear Studio`;
    return () => {
      document.title = "Bear Studio";
    };
  }, [project.name]);

  // Migrate old Record<string, string> files to PlayFileInode format
  useEffect(() => {
    const needsMigration = Object.values(project.files).some(
      (v) => typeof v === "string",
    );
    if (needsMigration) {
      changeProject((doc) => {
        migrateProjectFiles(doc);
      });
    }
  }, [project.files, changeProject]);

  const handleRename = useCallback(
    (nextName: string) => {
      changeProject((doc) => {
        doc.name = nextName;
      });
    },
    [changeProject],
  );

  const handleReturnToDashboard = useCallback(() => {
    router.navigate({ to: "/" });
  }, [router]);

  const deleteProjectMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/projects/${projectId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete project");
    },
    onSuccess: () => {
      toast.success("Project deleted");
      router.navigate({ to: "/" });
    },
    onError: () => {
      toast.error("Failed to delete project");
    },
  });

  const [isFilesPaneOpen, setIsFilesPaneOpen] = useState(true);
  const [isBuilderPaneOpen, setIsBuilderPaneOpen] = useState(true);
  const [filesPanelManualSize, setFilesPanelManualSize] = useState<number>();
  const [builderPanelManualSize, setBuilderPanelManualSize] =
    useState<number>();
  const [fileTabs, setFileTabs] = useState<FileTab[]>([]);
  const [dirtyFiles, setDirtyFiles] = useState<Set<string>>(new Set());
  const [openToolIds, setOpenToolIds] = useState<string[]>([]);
  const [focusedPaneId, setFocusedPaneId] = useState<string>(
    COMMAND_CENTER_PANE_ID,
  );
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [mosaicLayout, setMosaicLayout] = useState<MosaicNode<PaneId> | null>(
    null,
  );
  const { focusPane: focusWorkspacePane, blurFocusedPane } = useWorkspace();
  const repo = useContext(RepoContext);
  const { run, playground } = useBearSandbox();
  // Which directories are open. Held here, not in the tree, because it is what
  // decides which mounts get listed at all.
  const expansion = useTreeExpansion();
  // The doc tree with an opened mount's children (a published package's
  // sources) spliced in, so the list shows library content the doc can't hold.
  const treeFiles = useMaterializedFiles(
    project.files,
    playground,
    expansion.expanded,
  );
  // Dot-entries hide by default, with the OS chords (⌘⇧. / Ctrl+H) as the
  // reveal. One listener per project view.
  const { showHidden: showHiddenFiles } = useHiddenFiles();
  const handleRun = useCallback(async () => {
    await run();
  }, [run]);
  const commandDialogInputRef = useRef<HTMLInputElement>(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  // TODO: This is a rube-goldberg state machine. Make it an actual state machine.
  const commandPaletteLastFocusRef = useRef<string | null>(null);
  const commandPaletteSelectionMadeRef = useRef(false);
  const previousCommandPaletteOpenRef = useRef(false);

  const allPaneIds = useMemo(
    () => [...openToolIds, ...fileTabs.map((tab) => tab.id)],
    [fileTabs, openToolIds],
  );
  const hasOpenPanes = allPaneIds.length > 0;

  useEffect(() => {
    setMosaicLayout((previous) =>
      adjustLayoutForActivePanes(previous, allPaneIds),
    );
  }, [allPaneIds]);

  useEffect(() => {
    focusWorkspacePane(focusedPaneId);
  }, [focusWorkspacePane, focusedPaneId]);

  useEffect(() => {
    if (!isCommandPaletteOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      commandDialogInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isCommandPaletteOpen]);

  useEffect(() => {
    const wasOpen = previousCommandPaletteOpenRef.current;

    if (!wasOpen && isCommandPaletteOpen) {
      commandPaletteSelectionMadeRef.current = false;
      commandPaletteLastFocusRef.current = focusedPaneId;
      blurFocusedPane();
    } else if (wasOpen && !isCommandPaletteOpen) {
      if (
        !commandPaletteSelectionMadeRef.current &&
        commandPaletteLastFocusRef.current
      ) {
        focusWorkspacePane(commandPaletteLastFocusRef.current);
      }
      commandPaletteLastFocusRef.current = null;
    }

    previousCommandPaletteOpenRef.current = isCommandPaletteOpen;
  }, [
    blurFocusedPane,
    focusWorkspacePane,
    focusedPaneId,
    isCommandPaletteOpen,
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const mod = isMac ? event.metaKey : event.ctrlKey;
      if (mod) {
        const key = event.key.toLowerCase();
        if (key === "k") {
          event.preventDefault();
          setIsCommandPaletteOpen(true);
        } else if (key === "b") {
          event.preventDefault();
          setIsFilesPaneOpen((prev) => !prev);
        } else if (key === "j") {
          event.preventDefault();
          setIsBuilderPaneOpen((prev) => !prev);
        } else if (key === "enter") {
          event.preventDefault();
          handleRun();
        } else if (key === "s") {
          // Prevent browser save dialog — edits auto-sync via Automerge
          event.preventDefault();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMac, handleRun]);

  const handleOpenFile = useCallback(
    (
      filename: string,
      options?: {
        focus?: boolean;
        bypassValidation?: boolean;
        asPlainText?: boolean;
      },
    ) => {
      const kind = options?.asPlainText ? "plaintext" : getFileKind(filename);
      if (!kind) {
        return;
      }

      const shouldFocus = options?.focus ?? true;

      const tabId = getFileTabId(filename);
      setFileTabs((prev) => {
        if (prev.some((tab) => tab.id === tabId)) {
          return prev;
        }
        return [...prev, createFileTab(filename, kind, prev)];
      });
      if (shouldFocus) {
        setFocusedPaneId(tabId);
      }
    },
    [],
  );

  /**
   * Where a "go to definition" jump landed, and what it was aiming at.
   *
   * Held as one object rather than cleared after use: the editor reveals on
   * identity change, so a stale value sitting here is inert, and the same jump
   * repeated makes a new object and reveals again.
   */
  const [jumpTarget, setJumpTarget] = useState<{
    path: string;
    selection?: EditorSelection;
  } | null>(null);

  const handleOpenPath = useCallback(
    (path: string, selection?: EditorSelection) => {
      handleOpenFile(path);
      setJumpTarget({ path, selection });
    },
    [handleOpenFile],
  );

  const handleOpenAsPlainText = useCallback(
    (filename: string, options?: { focus?: boolean }) => {
      handleOpenFile(filename, { ...options, asPlainText: true });
    },
    [handleOpenFile],
  );

  const handleOpenHistory = useCallback(
    (filename: string, options?: { focus?: boolean }) => {
      const inode = resolveInode(project.files, filename);
      invariant(
        isAutomergeUrl(inode?.uri),
        "History is only available for Automerge documents.",
      );
      const shouldFocus = options?.focus ?? true;
      const tabId = getHistoryTabId(filename);
      setFileTabs((prev) => {
        if (prev.some((tab) => tab.id === tabId)) {
          return prev;
        }
        return [...prev, createFileTab(filename, "history", prev)];
      });
      if (shouldFocus) {
        setFocusedPaneId(tabId);
      }
    },
    [project.files],
  );

  // How the workspace puts itself back together after a file mutation,
  // wherever it came from: the row menu, the Files header, or a tool. The
  // shared commands fire these, so no call site has to remember.
  const fileMutated = useMemo(
    () => ({
      created: (path: string) =>
        handleOpenFile(path, { bypassValidation: true }),
      // A deleted file (or anything under a deleted folder) can't stay open.
      deleted: (path: string) =>
        setFileTabs((prev) =>
          prev.filter(
            (tab) =>
              tab.filename !== path && !tab.filename.startsWith(`${path}/`),
          ),
        ),
      // Renamed files keep their tab under the new path; a folder rename
      // carries every tab beneath it.
      renamed: (from: string, to: string) =>
        setFileTabs((prev) =>
          prev.map((tab) =>
            tab.filename === from
              ? createFileTab(to, tab.kind, prev)
              : tab.filename.startsWith(`${from}/`)
                ? createFileTab(
                    `${to}${tab.filename.slice(from.length)}`,
                    tab.kind,
                    prev,
                  )
                : tab,
          ),
        ),
    }),
    [handleOpenFile],
  );

  const commands = useFileCommands(playground, fileMutated);
  // A naming session has to be visible to be typed into, and any surface can
  // start one (row menu, header button, ⌘K tool). So the view reveals it (the
  // Files pane, and the directory it lands in) rather than each caller
  // remembering to.
  const editKey = commands.edit?.key ?? null;
  const editDir = commands.edit?.dir ?? null;
  const revealDir = expansion.reveal;
  useEffect(() => {
    if (editDir === null) return;
    setIsFilesPaneOpen(true);
    revealDir(editDir);
  }, [editKey, editDir, revealDir]);

  const handleOpenTool = useCallback(
    (toolId: string, options?: { focus?: boolean }) => {
      const tool = TOOL_BY_ID.get(toolId);
      if (!tool) {
        return;
      }

      if (tool.renderPane) {
        const shouldFocus = options?.focus ?? true;
        setOpenToolIds((prev) => {
          if (prev.includes(toolId)) {
            return prev;
          }
          return [...prev, toolId];
        });
        if (shouldFocus) {
          setFocusedPaneId(toolId);
        }
      }

      if (tool.activate) {
        tool.activate({
          commands,
          openUploadDialog: () => setIsUploadDialogOpen(true),
        });
      }
    },
    [commands],
  );

  // Wire floorplan editor context to open the floorplan tool pane
  const { setOnOpenPane } = useFloorplanEditor();
  useEffect(() => {
    setOnOpenPane(() => handleOpenTool("floorplan-editor"));
    return () => setOnOpenPane(null);
  }, [handleOpenTool, setOnOpenPane]);

  const handleCloseTool = useCallback(
    (toolId: string) => {
      setOpenToolIds((prev) => {
        if (!prev.includes(toolId)) {
          return prev;
        }
        const next = prev.filter((id) => id !== toolId);
        if (focusedPaneId === toolId) {
          const fallbackId =
            next[0] ?? fileTabs[0]?.id ?? COMMAND_CENTER_PANE_ID;
          setFocusedPaneId(fallbackId);
        }
        return next;
      });
    },
    [fileTabs, focusedPaneId],
  );

  const handleCloseFileTab = useCallback(
    (tabId: string) => {
      setFileTabs((prev) => {
        const index = prev.findIndex((tab) => tab.id === tabId);
        if (index === -1) {
          return prev;
        }
        const nextTabs = [...prev.slice(0, index), ...prev.slice(index + 1)];
        if (focusedPaneId === tabId) {
          const fallbackTab = nextTabs[index] ?? nextTabs[index - 1];
          const fallbackTabId =
            fallbackTab?.id ?? openToolIds[0] ?? COMMAND_CENTER_PANE_ID;
          setFocusedPaneId(fallbackTabId);
        }
        return nextTabs;
      });
    },
    [focusedPaneId, openToolIds],
  );

  const handleCloseHistoryTab = useCallback(
    (tabId: string) => {
      setFileTabs((prev) => {
        const index = prev.findIndex((tab) => tab.id === tabId);
        if (index === -1) {
          return prev;
        }
        const nextTabs = [...prev.slice(0, index), ...prev.slice(index + 1)];
        if (focusedPaneId === tabId) {
          const fallbackTab = nextTabs[index] ?? nextTabs[index - 1];
          const fallbackTabId =
            fallbackTab?.id ?? openToolIds[0] ?? COMMAND_CENTER_PANE_ID;
          setFocusedPaneId(fallbackTabId);
        }
        return nextTabs;
      });
    },
    [focusedPaneId, openToolIds],
  );

  const handleCreateCheckpoint = useCallback(
    async (description: string) => {
      if (!repo) {
        throw new Error("Automerge repo is not available");
      }

      // Minted single-writer by the server on project load; clients only read
      // it (a client-side mint would race two peers into rival refs docs).
      const checkpointsDocUrl = project.checkpointsDocUrl;
      if (!checkpointsDocUrl) {
        throw new Error(
          "Checkpoints are still initializing for this project — please try again in a moment.",
        );
      }
      await captureAndCreateCheckpoint(
        repo,
        checkpointsDocUrl,
        project.files,
        "/",
        description,
      );
    },
    [project.checkpointsDocUrl, project.files, repo],
  );

  const files = Object.keys(project.files).sort((a, b) => a.localeCompare(b));

  const commandGroups = useMemo<CommandMenuGroup[]>(() => {
    const toolItems = projectTools.map((tool) => {
      const Icon = tool.icon;
      return {
        id: tool.id,
        label: tool.commandLabel,
        description: tool.description,
        icon: <Icon className="h-4 w-4" />,
        keywords: tool.keywords,
        run: () => handleOpenTool(tool.id),
      };
    });

    const fileItems = files.map((filename) => ({
      id: `file:${filename}`,
      label: filename,
      icon: <ProjectFileIcon filename={filename} className="h-4 w-4" />,
      keywords: [filename],
      run: () => handleOpenFile(filename),
    }));

    const settingsItems = [
      {
        id: "toggle-vim-mode",
        label: vimMode ? "Disable Vim Mode" : "Enable Vim Mode",
        icon: <KeyboardIcon className="h-4 w-4" />,
        keywords: ["vim", "editor", "keybindings", "mode"],
        run: () => setVimMode(!vimMode),
      },
    ];

    return [
      { id: "tools", label: "Tools", items: toolItems },
      { id: "files", label: "Files", items: fileItems },
      { id: "settings", label: "Settings", items: settingsItems },
    ].filter((group) => group.items.length > 0);
  }, [files, handleOpenFile, handleOpenTool, vimMode, setVimMode]);

  const handleOpenCommandPalette = useCallback(() => {
    setIsCommandPaletteOpen(true);
  }, []);

  const handleCommandPaletteOpenChange = useCallback((open: boolean) => {
    setIsCommandPaletteOpen(open);
  }, []);

  const handleCommandPaletteItemSelected = useCallback(() => {
    commandPaletteSelectionMadeRef.current = true;
    setIsCommandPaletteOpen(false);
  }, []);

  const filesPanelDefaultSize = 15;
  const builderPanelDefaultSize = 24;
  const filesPanelSize = filesPanelManualSize ?? filesPanelDefaultSize;
  const builderPanelSize = builderPanelManualSize ?? builderPanelDefaultSize;
  const mainPanelDefaultSize = Math.max(
    0,
    100 -
      (isFilesPaneOpen ? filesPanelSize : 0) -
      (isBuilderPaneOpen ? builderPanelSize : 0),
  );

  const openFileNames = useMemo(
    () => [
      ...new Set(
        fileTabs
          .filter((tab) => tab.kind !== "history")
          .map((tab) => tab.filename),
      ),
    ],
    [fileTabs],
  );

  const activeFile = useMemo(() => {
    if (focusedPaneId.startsWith(FILE_TAB_PREFIX)) {
      return focusedPaneId.slice(FILE_TAB_PREFIX.length);
    }
    return null;
  }, [focusedPaneId]);

  useEffect(() => {
    if (hasOpenPanes) {
      if (!allPaneIds.includes(focusedPaneId)) {
        const fallbackId = allPaneIds[0] ?? COMMAND_CENTER_PANE_ID;
        setFocusedPaneId(fallbackId);
      }
    } else if (focusedPaneId !== COMMAND_CENTER_PANE_ID) {
      setFocusedPaneId(COMMAND_CENTER_PANE_ID);
    }
  }, [allPaneIds, focusedPaneId, hasOpenPanes]);

  const renderPane = useCallback(
    (paneId: PaneId, path: MosaicPath) => {
      const tool = TOOL_BY_ID.get(paneId);
      if (tool && tool.renderPane) {
        const IconComponent = tool.icon;
        return (
          <MosaicWindow
            path={path}
            title={tool.title}
            draggable={false}
            className="h-full"
            renderToolbar={() => (
              <PaneToolbar
                title={tool.title}
                icon={
                  <IconComponent className="h-4 w-4 text-muted-foreground" />
                }
                actions={
                  tool.id === "floorplan-editor" ? (
                    <FloorplanSaveButton />
                  ) : null
                }
                onClose={() => handleCloseTool(tool.id)}
                onFocus={() => setFocusedPaneId(tool.id)}
              />
            )}
          >
            <div
              className="flex h-full flex-col"
              onPointerDownCapture={() => setFocusedPaneId(tool.id)}
            >
              <PaneSuspense>
                {tool.renderPane({
                  project,
                  changeProject,
                  indexUrl,
                  onOpenPath: handleOpenPath,
                })}
              </PaneSuspense>
            </div>
          </MosaicWindow>
        );
      }

      const fileTab = fileTabs.find((tab) => tab.id === paneId);
      if (fileTab) {
        const inode = resolveInode(project.files, fileTab.filename);
        const uri = inode?.uri;
        const automergeUrl = isAutomergeUrl(uri)
          ? (uri as AutomergeUrl)
          : undefined;
        const externalHref = resolveProjectFileHref(uri);
        const typeLabel =
          fileTab.kind === "doc"
            ? "Document"
            : fileTab.kind === "code" || fileTab.kind === "plaintext"
              ? "Source"
              : fileTab.kind === "sketch"
                ? "Sketch"
                : "History";
        const fallbackContent = (
          <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
            {externalHref ? (
              <a
                href={externalHref}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Open {fileTab.filename}
              </a>
            ) : (
              `${typeLabel} file "${fileTab.filename}" not found.`
            )}
          </div>
        );
        let content: ReactNode;
        if (fileTab.kind === "history") {
          content = automergeUrl ? (
            <HistoryPane docName={fileTab.filename} docUrl={automergeUrl} />
          ) : (
            <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
              History is only available for Automerge documents.
            </div>
          );
        } else if (fileTab.kind === "doc") {
          // The rich editor needs a doc to write to. Markdown that has none
          // (a mounted package's README) still reads fine as source, so fall
          // through to the same read-only editor the code path uses rather
          // than telling the user it isn't there.
          content = automergeUrl ? (
            <DocPane
              paneId={fileTab.id}
              filename={fileTab.filename}
              url={automergeUrl}
            />
          ) : (
            <SourcePaneWithDirty
              paneId={fileTab.id}
              filename={fileTab.filename}
              setDirtyFiles={setDirtyFiles}
              onOpenPath={handleOpenPath}
              revealSelection={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.selection
                  : undefined
              }
            />
          );
        } else if (fileTab.kind === "code" || fileTab.kind === "plaintext") {
          // Always the source pane, with or without a doc behind it: a path
          // with no inode in the project doc is usually a mounted library
          // file, and the pane reads that through the filesystem and shows it
          // read-only. It falls back to "not found" itself when there is
          // genuinely nothing there.
          content = (
            <SourcePaneWithDirty
              paneId={fileTab.id}
              filename={fileTab.filename}
              url={automergeUrl}
              setDirtyFiles={setDirtyFiles}
              onOpenPath={handleOpenPath}
              revealSelection={
                jumpTarget?.path === fileTab.filename
                  ? jumpTarget.selection
                  : undefined
              }
            />
          );
        } else {
          content = automergeUrl ? (
            <SketchPane
              paneId={fileTab.id}
              filename={fileTab.filename}
              url={automergeUrl}
            />
          ) : (
            fallbackContent
          );
        }
        return (
          <MosaicWindow
            path={path}
            title={fileTab.title}
            draggable={false}
            className="h-full"
            renderToolbar={() => (
              <PaneToolbar
                title={fileTab.title}
                dirty={dirtyFiles.has(fileTab.filename)}
                icon={
                  fileTab.kind === "history" ? (
                    <History className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ProjectFileIcon
                      filename={fileTab.filename}
                      className="h-4 w-4 text-muted-foreground"
                    />
                  )
                }
                actions={
                  automergeUrl && fileTab.kind !== "history" ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      title="View history"
                      onClick={() => handleOpenHistory(fileTab.filename)}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  ) : null
                }
                onClose={() =>
                  fileTab.kind === "history"
                    ? handleCloseHistoryTab(fileTab.id)
                    : handleCloseFileTab(fileTab.id)
                }
                onFocus={() => setFocusedPaneId(fileTab.id)}
              />
            )}
          >
            <div
              className="flex h-full flex-col"
              onPointerDownCapture={() => setFocusedPaneId(fileTab.id)}
            >
              <PaneSuspense>{content}</PaneSuspense>
            </div>
          </MosaicWindow>
        );
      }

      return (
        <MosaicWindow
          path={path}
          title="Pane unavailable"
          draggable={false}
          className="h-full"
          renderToolbar={() => (
            <PaneToolbar
              title="Pane unavailable"
              onClose={() => {
                setMosaicLayout((prev) =>
                  adjustLayoutForActivePanes(
                    prev,
                    allPaneIds.filter((id) => id !== paneId),
                  ),
                );
              }}
              onFocus={() => {
                /* noop */
              }}
            />
          )}
        >
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Unable to load pane {paneId}
          </div>
        </MosaicWindow>
      );
    },
    [
      allPaneIds,
      fileTabs,
      dirtyFiles,
      handleCloseHistoryTab,
      handleCloseFileTab,
      handleCloseTool,
      handleOpenHistory,
      handleOpenPath,
      jumpTarget,
      project,
      changeProject,
      indexUrl,
      setFocusedPaneId,
    ],
  );

  return (
    <>
      <Header
        projectName={project.name}
        isFilesPaneOpen={isFilesPaneOpen}
        onToggleFilesPane={() => setIsFilesPaneOpen((prev) => !prev)}
        isBuilderPaneOpen={isBuilderPaneOpen}
        onToggleBuilderPane={() => setIsBuilderPaneOpen((prev) => !prev)}
        onRename={handleRename}
        onReturnToDashboard={handleReturnToDashboard}
        onDeleteProject={() => setIsDeleteDialogOpen(true)}
        onOpenCommandPalette={handleOpenCommandPalette}
        hasPendingChanges={dirtyFiles.size > 0}
        onRun={handleRun}
      />
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &ldquo;{project.name}&rdquo; and all
              of its files. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={deleteProjectMutation.isPending}
              onClick={() => deleteProjectMutation.mutate()}
            >
              {deleteProjectMutation.isPending
                ? "Deleting..."
                : "Delete project"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CommandDialog
        open={isCommandPaletteOpen}
        onOpenChange={handleCommandPaletteOpenChange}
      >
        <ProjectCommandMenu
          groups={commandGroups}
          inputRef={commandDialogInputRef}
          placeholder="Search tools and files..."
          onItemSelected={handleCommandPaletteItemSelected}
          listClassName="max-h-[300px]"
        />
      </CommandDialog>
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-hidden">
          <ResizablePanelGroup orientation="horizontal">
            {isFilesPaneOpen ? (
              <ResizablePanel
                id="files"
                defaultSize={filesPanelSize}
                minSize={10}
                onResize={(panelSize) => {
                  setFilesPanelManualSize(panelSize.asPercentage);
                }}
              >
                <ProjectFileList
                  files={treeFiles}
                  commands={commands}
                  expansion={expansion}
                  onOpenFile={handleOpenFile}
                  onOpenAsPlainText={handleOpenAsPlainText}
                  onOpenHistory={handleOpenHistory}
                  onCreateCheckpoint={handleCreateCheckpoint}
                  openFiles={openFileNames}
                  activeFile={activeFile ?? undefined}
                  showHidden={showHiddenFiles}
                  uploadDialogOpen={isUploadDialogOpen}
                  onUploadDialogOpenChange={setIsUploadDialogOpen}
                />
              </ResizablePanel>
            ) : null}
            {isFilesPaneOpen ? <ResizableHandle withHandle /> : null}
            <ResizablePanel
              id="main"
              defaultSize={mainPanelDefaultSize}
              minSize={30}
            >
              <div className="flex h-full flex-col">
                <Mosaic
                  className="mosaic mosaic-shadcn-theme h-full"
                  renderTile={renderPane}
                  value={hasOpenPanes ? mosaicLayout : null}
                  onChange={setMosaicLayout}
                  zeroStateView={
                    <PaneSuspense>
                      <CommandCenterPane
                        paneId={COMMAND_CENTER_PANE_ID}
                        groups={commandGroups}
                        placeholder="Search tools and files..."
                        onItemSelected={handleCommandPaletteItemSelected}
                      />
                    </PaneSuspense>
                  }
                />
              </div>
            </ResizablePanel>

            {isBuilderPaneOpen ? <ResizableHandle withHandle /> : null}
            {isBuilderPaneOpen ? (
              <ResizablePanel
                id="builder"
                defaultSize={builderPanelSize}
                minSize={builderPanelDefaultSize}
                onResize={(panelSize) => {
                  setBuilderPanelManualSize(panelSize.asPercentage);
                }}
              >
                <div className="flex h-full flex-col">
                  <PaneSuspense>
                    <BuilderPane
                      project={project}
                      indexUrl={indexUrl}
                      activeFile={activeFile}
                    />
                  </PaneSuspense>
                </div>
              </ResizablePanel>
            ) : null}
          </ResizablePanelGroup>
        </main>

        {/*<TanStackRouterDevtools /> */}
      </div>
    </>
  );
}

function SourcePaneWithDirty({
  paneId,
  filename,
  url,
  setDirtyFiles,
  onOpenPath,
  revealSelection,
}: {
  paneId: string;
  filename: string;
  /** Absent for a file with no Automerge doc, e.g. mounted library content. */
  url?: AutomergeUrl;
  setDirtyFiles: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Open another workspace file, e.g. a "go to definition" target. */
  onOpenPath: (path: string, selection?: EditorSelection) => void;
  /** Where to put the cursor once this file loads (a jump landed here). */
  revealSelection?: EditorSelection;
}) {
  const handleDirtyChange = useCallback(
    (dirty: boolean) => {
      setDirtyFiles((prev) => {
        const has = prev.has(filename);
        if (dirty === has) return prev;
        const next = new Set(prev);
        if (dirty) {
          next.add(filename);
        } else {
          next.delete(filename);
        }
        return next;
      });
    },
    [filename, setDirtyFiles],
  );

  return (
    <SourcePane
      paneId={paneId}
      filename={filename}
      url={url}
      onDirtyChange={handleDirtyChange}
      onOpenPath={onOpenPath}
      revealSelection={revealSelection}
    />
  );
}

function FloorplanSaveButton() {
  const { saveRef } = useFloorplanEditor();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      title="Save floor plan"
      onClick={() => saveRef.current?.()}
    >
      <SaveIcon className="h-4 w-4" />
    </Button>
  );
}

function PaneToolbar({
  title,
  icon,
  dirty,
  actions,
  onClose,
  onFocus,
}: {
  title: string;
  icon?: ReactNode;
  dirty?: boolean;
  actions?: ReactNode;
  onClose: () => void;
  onFocus: () => void;
}) {
  const windowContext = useContext(MosaicWindowContext);

  const connectDragSource =
    windowContext?.mosaicWindowActions.connectDragSource;

  const dragRegion = (
    <div className="flex h-full min-w-0 flex-1 cursor-grab select-none items-center gap-2 pr-2 text-sm font-medium text-foreground">
      <span className="relative shrink-0">
        {icon}
        {dirty ? (
          <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-foreground" />
        ) : null}
      </span>
      <span className="truncate">{title}</span>
    </div>
  );

  const dragSource = connectDragSource
    ? connectDragSource(dragRegion)
    : dragRegion;

  return (
    <div
      className="flex h-10 w-full items-center border-b border-border bg-card px-2"
      onPointerDownCapture={onFocus}
    >
      {dragSource}
      {actions ? (
        <div className="flex items-center gap-1 pr-1">{actions}</div>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        title="Close pane"
        onClick={onClose}
      >
        <XIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

function adjustLayoutForActivePanes(
  layout: MosaicNode<PaneId> | null,
  panes: PaneId[],
): MosaicNode<PaneId> | null {
  if (panes.length === 0) {
    return null;
  }

  const allowed = new Set<PaneId>(panes);
  const pruned = pruneLayout(layout, allowed);
  const existing = pruned ? getLeaves(pruned) : [];
  const missing = panes.filter((pane) => !existing.includes(pane));

  let next = pruned;
  for (const pane of missing) {
    next = insertPaneIntoLayout(next, pane);
  }

  return next;
}

function pruneLayout(
  node: MosaicNode<PaneId> | null,
  allowed: Set<PaneId>,
): MosaicNode<PaneId> | null {
  if (!node) {
    return null;
  }
  if (typeof node === "string") {
    return allowed.has(node as PaneId) ? (node as PaneId) : null;
  }
  if (node.type !== "split") {
    // Tabs nodes are never produced by this app; leave them untouched.
    return node;
  }

  const prunedChildren = node.children
    .map((child) => pruneLayout(child, allowed))
    .filter((child): child is MosaicNode<PaneId> => child !== null);

  if (prunedChildren.length === 0) {
    return null;
  }
  if (prunedChildren.length === 1) {
    return prunedChildren[0];
  }

  const unchanged =
    prunedChildren.length === node.children.length &&
    prunedChildren.every((child, index) => child === node.children[index]);
  if (unchanged) {
    return node;
  }

  // Children changed, so the stored split percentages no longer line up;
  // drop them and let the remaining children default to equal sizing.
  return {
    type: "split",
    direction: node.direction,
    children: prunedChildren,
  };
}

function insertPaneIntoLayout(
  layout: MosaicNode<PaneId> | null,
  pane: PaneId,
): MosaicNode<PaneId> | null {
  if (!layout) {
    return pane;
  }

  if (typeof layout === "string" || layout.type !== "split") {
    return {
      type: "split",
      direction: "row",
      children: [layout, pane],
    };
  }

  const direction = layout.direction === "row" ? "column" : "row";

  return {
    type: "split",
    direction,
    children: [layout, pane],
  };
}
